import to from 'await-to-js';
import { Node } from 'contensis-management-api';
import pl from 'pluralize';

import EntriesMigrationService from './EntriesMigrationService';
import { MigrateRequest } from '~/models/Operations';
import {
  logEntitiesTable,
  logMigrateResultsTable,
  logNodesPreviewTable,
} from '~/transformations/logging';
import { mapNodesMigrateResult } from '~/transformations/mapNodesMigrateResult';
import { NodeBuilder } from '~/transformations/NodeBuilder';
import { completeProgressBar, Logger, newProgressBar } from '~/util/logger';

class NodesMigrationService extends EntriesMigrationService {
  get errorCount() {
    return Object.keys(this.errors).length;
  }

  get rootId() {
    return this.payload.query?.includeIds?.[0];
  }

  get rootPath() {
    return this.rootPaths?.[0] || '/';
  }

  get rootPaths() {
    return this.payload.query?.includePaths || ['/'];
  }

  constructor(payload: MigrateRequest, preview: boolean) {
    super(payload, preview);
  }

  DeleteNodes = async (rootPaths = this.rootPaths) => {
    await this.GetNodesToDelete(rootPaths);

    if (!this.isPreview) {
      for (const [projectId, target] of Object.entries(this.targets)) {
        Logger.info(
          `[nodes] ${projectId} | Deleting nodes on ${
            target.alias
          } at ${rootPaths.join(', ')}`
        );
        // Check if root node id is the same as the one in raw
        const targetRootNode = target.nodes.treeRootNode as Node;

        // If root node is tree root we need to delete the first level of children and
        // update the tree root
        const treeRoot = target.nodes.raw.find((n) => !n.parentId);
        if (treeRoot?.id === targetRootNode.id) {
          const targetRootNodeChildren = await target.repo.GetNodeChildren(
            targetRootNode.id
          );

          const totalToDelete = target.nodes.migrateNodes.filter((n) =>
            ['delete', 'update'].includes(n.status)
          ).length;
          const pr = newProgressBar(
            ` Deleting nodes [${
              target.project.id
            }] :bar :percent ${Logger.infoText(
              ':current/:total :elapseds/:etas :ratep/s'
            )}`,
            {
              output: this.payload.outputProgress && totalToDelete > 9,
              complete: Logger.successText('='),
              head: Logger.successText('>'),
              incomplete: ' ',
              width: 30,
              total: totalToDelete,
            }
          );

          if (targetRootNode.entryId) {
            Logger.info(
              `[nodes] ${projectId} | Clearing entry from root node on ${target.alias}`
            );
            targetRootNode.entryId = undefined;
            const [error, updatedRootNode] = await to(
              target.repo.UpdateNode(targetRootNode)
            );
            pr?.tick();
            if (error) this.AddError(targetRootNode.id, error);
            else if (updatedRootNode)
              this.nodesMigration.updated.push(updatedRootNode.id);
          }
          Logger.info(
            `[nodes] ${projectId} | Deleting root node children on ${target.alias}`
          );
          for (const childNode of targetRootNodeChildren) {
            const [deleteError] = await target.repo.DeleteNode(childNode.id);
            if (this.payload.outputProgress) pr?.tick();
            if (deleteError) this.AddError(childNode.id, deleteError);
            else this.nodesMigration.deleted.push(childNode.id);
          }
          completeProgressBar();
        } else {
          // root node is not in the list so we can just delete the remaining nodes
          for (const node of target.nodes.raw) {
            Logger.warning(
              `Deleting node at ${Logger.standardText(node.path)}${
                node.childCount
                  ? ` (including ${pl('child', node.childCount, true)})`
                  : ''
              } from  ${projectId} on ${target.alias}`
            );
            const [deleteError] = await target.repo.DeleteNode(node.id);
            if (deleteError) this.AddError(node.id, deleteError);
            else this.nodesMigration.deleted.push(node.id);
          }
        }
      }
    }

    Logger.success(
      `Delete nodes ${this.isPreview ? 'preview ready' : 'completed'}`,
      true
    );
    return mapNodesMigrateResult(this);
  };

  GetNodesToDelete = async (rootPaths: string[]) => {
    const promises = [] as Promise<any>[];

    for (const target of Object.values(this.targets)) {
      target.nodes.treeRootNode = await target.repo.GetRootNode();
      for (const rootPath of rootPaths) {
        promises.push(
          (async () => {
            const [err] = await target.nodes.GetNodes(
              rootPath,
              ['/', target.nodes.treeRootPath].includes(rootPath) ? 1 : 0
            );
            if (err) this.AddError(rootPath, err);

            for (const node of target.nodes.raw) {
              const nodeBuilder = new NodeBuilder(target, this.source, node, {
                onError: (n) => this.AddError(n.originalId, n.error),
              });
              nodeBuilder.existing = node as any;
              nodeBuilder.status =
                nodeBuilder.path === target.nodes.treeRootPath &&
                !nodeBuilder.parentId
                  ? nodeBuilder.entryId
                    ? 'update'
                    : 'no change'
                  : 'delete';
              target.nodes.migrateNodes.push(nodeBuilder);

              // target.nodes.raw.map((n) => ({
              //   childCount: n.childCount,
              //   displayName: (n as any).title || n.displayName,
              //   entryId: (n as any).entryId,
              //   existing: n,
              //   id: n.id,
              //   node: n as any,
              //   originalId: n.id,
              //   path: n.path,
              //   parentId: n.parentId,
              //   status:
              //     n.path === target.nodes.treeRootPath && !n.parentId
              //       ? n.entry
              //         ? 'update'
              //         : 'no change'
              //       : 'delete',
              // })) || [];
            }
          })()
        );
      }
    }
    // Get nodes from all repos at the same time
    await Promise.all(promises);

    for (const target of Object.values(this.targets)) {
      logEntitiesTable({
        nodes: target.nodes.raw,
        projectId: target.projectId,
      });
    }
  };

  MigrateNodes = async (rootPath = this.rootPath) => {
    // Loads node tree from source and target repos into memory
    await this.GetNodes(rootPath);

    // Build and store the target nodes
    // against each targetRepo.migrateNodes
    await this.BuildNodesToMigrate();

    // Check that entries mapped into built nodes exist
    await this.GetNodeEntries();

    let result = mapNodesMigrateResult(this);
    logNodesPreviewTable(result, Object.keys(this.targets));

    if (!this.isPreview && this.errorCount && !this.ignoreErrors) {
      Logger.error(
        `[nodes] Not committing ${this.opName} due to ${this.errorCount} errors`
      );
    } else if (!this.isPreview) {
      if (this.ignoreErrors)
        Logger.warning(
          `[nodes] Committing ${this.opName} with ${this.errorCount} known errors`
        );
      // Queueing does not work as we need to load the nodes in the right order
      // so parents can be loaded before children
      // this.QueueNodes();
      await this.ProcessNodes();
    }

    result = mapNodesMigrateResult(this);
    logMigrateResultsTable(result, 'nodes');

    Logger.success(
      `Nodes ${this.opName} ${this.isPreview ? 'preview ready' : 'complete'}`,
      true
    );
    return result;
  };

  GetNodes = async (
    rootPath?: string,
    depth: number | undefined = this.payload.stopLevel
  ) => {
    const { source, targets } = this;
    // Get nodes from source repo
    if (source.repo) {
      Logger.info(
        `Fetching initial nodes at ${
          rootPath || this.rootId || this.rootPath
        } in ${source.alias} project '${source.projectId}'`,
        true
      );
      const [err] = await source.nodes.GetNodes(
        {
          path: rootPath || (this.rootId ? '' : this.rootPath),
          id: this.rootId,
        },
        // If nodes are supplied in the payload only get root node
        source.nodes.raw?.length ? 0 : depth
      );
      if (err) throw err;
      await source.nodes.GetNodeAncestors();
    }
    if (Logger.shouldLog('info'))
      logEntitiesTable({
        nodes: source.nodes.raw as any,
        projectId: source.projectId,
      });

    for (const [, target] of Object.entries(targets)) {
      await target.nodes.fetchTargetNodes(
        target.nodes.rootNode?.path || this.rootPath,
        undefined,
        depth
      );
    }
    return source.nodes.raw;
  };

  GetNodeEntries = async ({
    ignoreErrors = this.ignoreErrors,
  }: {
    ignoreErrors?: boolean;
  } = {}) => {
    for (const [, target] of Object.entries(this.targets)) {
      await target.nodes.ValidateNodeEntries({
        ignoreErrors,
        onError: (migrateNode) => {
          this.AddError(migrateNode.originalId, migrateNode.error);
        },
      });
    }
  };

  ProcessNodes = async () => {
    for (const [, target] of Object.entries(this.targets)) {
      // Delegated to super->EntriesMigrationService
      await this.CommitNodes(target);
    }
  };
}

export default NodesMigrationService;
